import { useEffect } from 'react'
import { useSnackbar } from '@shared/ui/notification-context'
import {
  getAllShortVideoSubscribes,
  getCurrentUserVideoBookmarks,
  getPublishedShortVideoChannels,
  getPublishedShortVideoTopics,
  getPublishedShortVideosPage,
  subscribeToShortVideoLikes,
} from '@services/firebase/short-videos'
import { ShortVideosStateProvider } from './state/ShortVideosStateProvider'
import { useShortVideosState } from './state/useShortVideosState'
import { ShortVideoListScreen } from './catalog/ShortVideoListScreen'
import { ShortVideoDetailScreen } from './detail/ShortVideoDetailScreen'
import { ShortVideosStatusBlock } from './shared/ShortVideosStatusBlock'
import './short-videos.css'

function ShortVideosFeatureShell() {
  const { enqueueSnackbar } = useSnackbar()
  const { actions, state } = useShortVideosState()

  useEffect(() => {
    let isMounted = true

    const bootstrap = async () => {
      try {
        actions.setBootstrapLoading(true)
        actions.setErrorMessage(null)

        const [page, topics, channels, bookmarks, subscribes] = await Promise.all([
          getPublishedShortVideosPage({}),
          getPublishedShortVideoTopics(),
          getPublishedShortVideoChannels(),
          getCurrentUserVideoBookmarks(),
          getAllShortVideoSubscribes(),
        ])

        if (!isMounted) {
          return
        }

        actions.setVideos(page.videos)
        actions.setTopics(topics)
        actions.setChannels(channels)
        actions.setBookmarks(bookmarks)
        actions.setSubscribes(subscribes)
        actions.setPagination({
          canGoNext: page.videos.length < page.total,
          canGoPrev: false,
          historySizes: [],
          offset: 0,
          total: page.total,
        })
      } catch (error) {
        if (!isMounted) {
          return
        }

        const message =
          error instanceof Error
            ? error.message
            : 'Short-video data failed to load. Please try again.'

        actions.setErrorMessage(message)
        enqueueSnackbar(message, { variant: 'error' })
      } finally {
        if (isMounted) {
          actions.setBootstrapLoading(false)
        }
      }
    }

    void bootstrap()

    const unsubscribe = subscribeToShortVideoLikes((likes) => {
      actions.setLikes(likes)
    })

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [actions, enqueueSnackbar])

  const isFeedPreparing = state.loading.bootstrap || (!state.feedLoaded && !state.errorMessage)

  if (isFeedPreparing) {
    return (
      <ShortVideosStatusBlock
        body="Keep this page open while we load videos, topics, and channels."
        minHeight={260}
        title="Preparing short videos"
        isLoading
      />
    )
  }

  if (state.errorMessage && !state.feedLoaded) {
    return (
      <ShortVideosStatusBlock
        body={state.errorMessage}
        minHeight={260}
        title="We could not load short videos"
        tone="error"
      />
    )
  }

  return state.selectedVideo ? <ShortVideoDetailScreen /> : <ShortVideoListScreen />
}

export function ShortVideosScreen() {
  return (
    <ShortVideosStateProvider>
      <div
        className="shortVideosFeatureCard"
        data-qa="short-videos-visual-shell"
      >
        <ShortVideosFeatureShell />
      </div>
    </ShortVideosStateProvider>
  )
}
